import React from 'react';

import type { Copro } from '../../../../@types/Copro';
import type { Owner } from '../../../../@types/Owner';

interface CoproLocalOwnedProps {
  owner: Owner;
}

/**
 * Component to display coproperty and property details with consistent styling
 */
const CoproLocalOwned: React.FC<CoproLocalOwnedProps> = ({ owner }) => {
  // Get property name (copro name)
  const getPropertyName = (ownerWithProperty: Owner): string => {
    // First, check for copro name
    if (ownerWithProperty.dpe?.copro?.name) {
      return ownerWithProperty.dpe.copro.name;
    }

    // Fallback to incoming call copro when no DPE
    if (ownerWithProperty.incomingCallCopro?.name) {
      return ownerWithProperty.incomingCallCopro.name;
    }
    
    // Next, try details from DPE
    if (ownerWithProperty.dpe?.details) {
      // Check if details is an array
      if (Array.isArray(ownerWithProperty.dpe.details) && ownerWithProperty.dpe.details.length > 0) {
        const dpeDetail = ownerWithProperty.dpe.details[0];
        return dpeDetail.labelBrut || dpeDetail.banLabel || dpeDetail.adresseBrut || 'Copropriété non renseignée';
      } else if (typeof ownerWithProperty.dpe.details === 'object') {
        const dpeDetail = ownerWithProperty.dpe.details as Record<string, unknown>;
        return String(dpeDetail.labelBrut ?? dpeDetail.banLabel ?? dpeDetail.adresseBrut ?? 'Copropriété non renseignée');
      }
    }
    
    // Fall back to infoDPE if available
    if (ownerWithProperty.dpe?.infoDPE?.info) {
      return String(ownerWithProperty.dpe.infoDPE.info);
    }
    
    // Default fallback
    return 'Copropriété non renseignée';
  };

  // Get property address
  const getFullAddress = (ownerWithAddress: Owner): string => {
    // Check if we have address from dpe.copro
    if (ownerWithAddress.dpe?.copro?.addresses && ownerWithAddress.dpe.copro.addresses.length > 0) {
      const coproAddress = ownerWithAddress.dpe.copro.addresses[0];
      return `${coproAddress.address || ''}, ${coproAddress.postalCode || ''} ${coproAddress.city || ''}`.trim();
    }

    // Fallback to incoming call copro address
    if (ownerWithAddress.incomingCallCopro) {
      const incoming = ownerWithAddress.incomingCallCopro as Copro & { address?: string; cp?: string; city?: string };
      if (incoming.addresses && Array.isArray(incoming.addresses) && incoming.addresses.length > 0) {
        const coproAddress = incoming.addresses[0];
        return `${coproAddress.address || ''}, ${coproAddress.postalCode || ''} ${coproAddress.city || ''}`.trim();
      }
      // Support flat fields from API as well
      const icAddress = incoming.address || '';
      const icPostalCode = incoming.cp || '';
      const icCity = incoming.city || '';
      const flat = `${icAddress}${icAddress ? ', ' : ''}${icPostalCode} ${icCity}`.trim();
      if (flat) {
        return flat;
      }
    }
    
    // Fall back to owner address
    const address = ownerWithAddress.address || '';
    const postalCode = ownerWithAddress.postalCode || '';
    const city = ownerWithAddress.city || '';
    return `${address}${address ? ', ' : ''}${postalCode} ${city}`.trim();
  };

  // Get property details (lot, floor, door, surface)
  const getPropertyDetails = (ownerWithDetails: Owner): string => {
    const lotNumber = ownerWithDetails.lotNumber ? `Lot ${ownerWithDetails.lotNumber}` : '';
    const floorNumber = ownerWithDetails.floorNumber ? `Étage ${ownerWithDetails.floorNumber}` : '';
    const doorNumber = ownerWithDetails.doorNumber ? `Porte ${ownerWithDetails.doorNumber}` : '';
    const surface = ownerWithDetails.surface ? `${ownerWithDetails.surface} m²` : '';
    
    return [lotNumber, floorNumber, doorNumber, surface]
      .filter(item => item)
      .join(' • ');
  };

  return (
    <div>
      <div className="mb-1 font-medium text-gray-900">
        {getPropertyName(owner)}
      </div>
      <div>{getFullAddress(owner)}</div>
      <div className="mt-1 text-sm text-gray-600">{getPropertyDetails(owner)}</div>
    </div>
  );
};

export default CoproLocalOwned; 